/**
 * Kleda Client App - Views
 * Each view corresponds to a phase in the order lifecycle
 *
 * V3 "Stil premium": elk scherm volgt hetzelfde taak-gedreven stramien —
 * menselijke kop + intro-zin met de kerninfo, één primaire taak-kaart,
 * secundaire acties als stille rijen (ActionList), trust-regel onderaan.
 * Timeline en OrderSummary worden door PortalLayout gerenderd.
 */

import { useState, useCallback, useEffect } from 'react'
import { KLEDA_CLAIMS, KLEDA_FAQ, KLEDA_CONTACT } from '@platform/kleda-content'
import type { OrderData, RouteOptie, PricingBootstrap, AddVloerkleedRequest, AnnuleerResponseData } from '../types'
import { fetchPricingBootstrap, addVloerkleed } from '../api'
import { openBetaalLink } from '../lib/betaal'
import { GegevensControleren } from './GegevensControleren'
import { PageLayout } from './Layout'
import {
  Card,
  StatusHeader,
  ActionList,
  ActionRow,
  TrustLine,
  OpmerkingenVeld,
  AfspraakInfo,
  AfspraakStatus,
  Icon,
  WijzigAfspraakModal,
  AnnulerenModal,
  VloerkleedToevoegenModal,
  SuccessConfirmation,
  CountdownTimer,
  PullToRefresh,
  OpslagkostenBadge,
  FaqList,
} from './UI'
import { ReviewCTA } from './ReviewCTA'
import { VolgendeKeerKaart, mijnKledaHref } from './VolgendeKeerKaart'
import { VlekkenbaasUpsell, vlekkenbaasTeBieden } from './VlekkenbaasUpsell'
import { OrderSummary } from './OrderSummary'
import { trackEvent } from '../analytics'


// Doorlooptijd 5 tot 10 werkdagen (SSOT) vanaf de ophaaldag, als datumvenster.
function plusWerkdagen(start: Date, n: number): Date {
  const d = new Date(start)
  let left = n
  while (left > 0) {
    d.setDate(d.getDate() + 1)
    if (d.getDay() !== 0 && d.getDay() !== 6) left--
  }
  return d
}

function verwachtKlaarVenster(ophalenVanaf?: string): { van: string; tot: string } | null {
  if (!ophalenVanaf) return null
  const start = new Date(ophalenVanaf)
  if (Number.isNaN(start.getTime())) return null
  const fmt = (d: Date) => d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'long' })
  return { van: fmt(plusWerkdagen(start, 5)), tot: fmt(plusWerkdagen(start, 10)) }
}

function formatTijd(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
}

/**
 * ISO-moment → yyyy-mm-dd in de tijdzone van de bezoeker, zodat het te
 * vergelijken is met `RouteOptie.datum`. Klanten zitten in NL, net als de rest
 * van de datumweergave in deze app (`toLocaleDateString` zonder timeZone).
 */
function datumSleutel(iso: string): string | undefined {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  const mm = `${d.getMonth() + 1}`.padStart(2, '0')
  const dd = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

// =============================================================================
// HELPERS
// =============================================================================

/** Strip HTML uit SF formula-adresvelden voor inline weergave */
function cleanAdres(adres?: string): string {
  return adres?.replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '') || ''
}

function formatEUR(bedrag: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(bedrag)
}

/** Permanente ingang naar een deelpagina van deze order (?view=…) */
function getViewHref(view: string): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  url.searchParams.set('view', view)
  return url.pathname + url.search
}

/**
 * Zit de order in de aflever-fase? Dan is het kleed gereinigd en is er beeld.
 *
 * Strikter dan het gebruikelijke `!isPickup` in dit bestand: `phaseType` kan
 * null zijn, en dat leest daar als "afleveren". Voor kopij is dat onschuldig,
 * voor een link naar foto's niet — die zou dan vóór de ophaal op een lege
 * pagina uitkomen.
 */
function isAfleverFase(order: OrderData): boolean {
  return order.phaseType === 'delivery'
}

// Kaart "Volgende reiniging" (plan 2026-10-22, herzien 2026-10-26) achter een
// flag. Na aflevering bovenaan; in de aflever-fase zodra er niets meer te
// betalen is (de server toetst zelf of kiezen daar al mag).
const TERUGKOM_KAART_AAN = import.meta.env.VITE_TERUGKOM_KAART_ENABLED === 'true'

/** Rij naar Mijn Kleda (alleen als de pagina aan staat en er een token is). */
function MijnKledaRij({ token }: { token?: string }) {
  const href = token ? mijnKledaHref(token) : null
  if (!href) return null
  return <ActionRow label="Mijn Kleda" sublabel="Je kleden, bestellingen en volgende reiniging" actionLabel="Openen" href={href} />
}

/**
 * Ingang naar de foto's en video's van de kleden.
 *
 * Heet naar wat erachter ligt en niet naar de werkstap waarin het beeld gemaakt
 * is: de pagina toont binnenkomst, reiniging én resultaat, terwijl deze rij
 * eerder "Voorinspectie-rapport" heette en dus alleen het eerste dekte.
 *
 * De href is statisch, zodat de orderpagina niets hoeft op te halen; ?view=fotos
 * zoekt zelf uit of het één kleed is (direct door) of meer (tegels). Daarom
 * staat deze rij alleen op views ná de ophaal — daarvóór bestaat er geen beeld
 * en zou de klant op een lege staat landen.
 */
function FotoRij() {
  const href = getViewHref('fotos')
  if (!href) return null
  return <ActionRow label="Foto's van je kleed" actionLabel="Bekijken" href={href} />
}

/**
 * Het inspectierapport blijft bereikbaar, maar als wat het is: het verhaal van
 * de binnenkomst. Het was de voordeur naar alle media en is nu de tweede rij.
 */
function VoorinspectieRij({ order }: { order: OrderData }) {
  const href = order.voorinspectie?.done ? getViewHref('voorinspectie') : null
  if (!href) return null
  return <ActionRow label="Wat we bij binnenkomst zagen" actionLabel="Bekijken" href={href} />
}

/** Stille adres-regel onder de kop */
function AdresRegel({ label, adres, onWijzig }: { label: string; adres?: string; onWijzig?: () => void }) {
  const clean = cleanAdres(adres)
  if (!clean) return null
  return (
    <p className="-mt-sm mb-lg text-[13px] text-ink-faint">
      {label}: <span className="text-ink-soft">{clean}</span>
      {onWijzig && (
        <>
          {' '}
          <button
            type="button"
            onClick={onWijzig}
            className="font-medium text-primary hover:text-primary-dark"
          >
            Wijzigen
          </button>
        </>
      )}
    </p>
  )
}

// Adres-formulier (parse/lookup/postcode-format) leeft nu in GegevensControleren.

// Per-fase FAQ-content leeft nu in @platform/kleda-content (KLEDA_FAQ.ophaal/
// .reinigen/.aflever) zodat alle surfaces dezelfde, merk-correcte antwoorden tonen.

// =============================================================================
// KIES AFSPRAAK VIEW
// =============================================================================

interface KiesAfspraakViewProps {
  order: OrderData
  token: string
  onKiesAfspraak: (routeId: string, opmerkingen?: string) => Promise<void>
  onLaadMeerOpties?: () => Promise<void>
  onRefresh?: () => void
  vlekkenbaasHighlighted?: boolean
  onVlekkenbaasSuccess?: () => void
  /** Bevestiging gelukt — App toont dan de success-stap (los van de order-
      status, die door de refetch direct naar 'ingepland' springt) */
  onAfspraakBevestigd?: (moment: string | null) => void
}

export function KiesAfspraakView({ order, token, onKiesAfspraak, onLaadMeerOpties, onRefresh, vlekkenbaasHighlighted, onVlekkenbaasSuccess, onAfspraakBevestigd }: KiesAfspraakViewProps) {
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null)
  const [opmerkingen, setOpmerkingen] = useState('')
  const [showOpmerkingen, setShowOpmerkingen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [visibleExtraCount, setVisibleExtraCount] = useState(0)
  const [extraLoaded, setExtraLoaded] = useState(false)
  const [isLading, setIsLading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Vloerkleed toevoegen state
  const [showVloerkleedModal, setShowVloerkleedModal] = useState(false)
  const [pricing, setPricing] = useState<PricingBootstrap | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(false)

  // Adres wijzigen state (formulier-state leeft in GegevensControleren)
  const [showAdresEdit, setShowAdresEdit] = useState(false)
  const [adresGewijzigd, setAdresGewijzigd] = useState(false)

  const isPickup = order.phaseType === 'pickup'
  const EXTRA_BATCH_SIZE = 5
  const extraOpties = order.extraRouteOpties ?? []
  const alleOpties = [...order.routeOpties, ...extraOpties.slice(0, visibleExtraCount)]
  const hasMoreExtra = visibleExtraCount < extraOpties.length
  const selectedOptie = alleOpties.find(o => o.routeId === selectedRouteId)

  // Aantal kleden (alleen primaire items — packages/extras tellen niet mee)
  const itemCount = order.items?.filter(i => i.productCategory === 'Primary').length || 0
  const itemTekst = itemCount === 1 ? 'vloerkleed' : 'vloerkleden'
  const enkelKleedTitel = itemCount <= 1

  // Laad pricing wanneer modal opent
  const handleOpenVloerkleedModal = useCallback(async () => {
    setShowVloerkleedModal(true)
    if (!pricing) {
      setIsLoadingPricing(true)
      try {
        const result = await fetchPricingBootstrap(token)
        setPricing(result)
      } catch {
        // Error handled in modal
      } finally {
        setIsLoadingPricing(false)
      }
    }
  }, [token, pricing])

  const handleAddVloerkleed = useCallback(async (request: Omit<AddVloerkleedRequest, 'token'>): Promise<boolean> => {
    const result = await addVloerkleed({ ...request, token })
    if (result.success) {
      // Refresh order data to show new item
      if (onRefresh) {
        onRefresh()
      }
      // Update pricing to reflect new counts
      setPricing(prev => prev ? {
        ...prev,
        bestaandeAantalKleden: prev.bestaandeAantalKleden + 1,
        bestaandeTotaalM2: result.nieuwTotaalM2 || prev.bestaandeTotaalM2
      } : null)
      return true
    } else {
      throw new Error(result.message || 'Er ging iets mis')
    }
  }, [token, onRefresh])

  async function handleLoadMore() {
    if (!extraLoaded && onLaadMeerOpties) {
      // Eerste keer: API call om extra opties te laden
      setIsLading(true)
      try {
        await onLaadMeerOpties()
        setExtraLoaded(true)
        setVisibleExtraCount(EXTRA_BATCH_SIZE)
      } finally {
        setIsLading(false)
      }
    } else {
      // Volgende keren: toon meer uit reeds geladen data (instant)
      setVisibleExtraCount(prev => prev + EXTRA_BATCH_SIZE)
    }
  }

  async function handleRefresh() {
    if (!onRefresh) return
    setIsRefreshing(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 500)) // Min delay for UX
      onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }

  function handleSelect(optie: RouteOptie) {
    // Altijd routeId gebruiken voor betrouwbare selectie
    if (optie.routeId) {
      setSelectedRouteId(optie.routeId)
    }
    setError(null)
  }

  function handleOpenAdresEdit() {
    setAdresGewijzigd(false)
    setShowAdresEdit(true)
  }

  function handleAdresOpgeslagen() {
    // Nieuwe regio kan andere routes betekenen: selectie en geladen
    // extra opties resetten, daarna de order (met verse momenten) herladen
    setSelectedRouteId(null)
    setExtraLoaded(false)
    setVisibleExtraCount(0)
    setShowAdresEdit(false)
    setAdresGewijzigd(true)
    onRefresh?.()
  }

  async function handleSubmit() {
    if (!selectedRouteId) {
      setError(`Selecteer een ${isPickup ? 'ophaal' : 'aflever'}moment`)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const gekozenOptie = alleOpties.find(o => o.routeId === selectedRouteId)

      await onKiesAfspraak(selectedRouteId, opmerkingen || undefined)

      // App toont de success-stap; die mag niet hier leven omdat de
      // refetch de status (en daarmee deze view) direct omklapt
      onAfspraakBevestigd?.(gekozenOptie?.label || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} isRefreshing={isRefreshing}>
      <div>
        <StatusHeader
          title={isPickup
            ? 'Wanneer mogen we langskomen?'
            : enkelKleedTitel
              ? 'Je kleed is klaar — wanneer mag het thuiskomen?'
              : 'Je kleden zijn klaar — wanneer mogen ze thuiskomen?'
          }
          subtitle={isPickup
            ? <>Kies het moment waarop we je {itemTekst} ophalen. De dag ervoor ontvang je per e-mail een tijdsblok van 2 uur.</>
            : <>Kies het moment waarop we je schone {itemTekst} brengen. De dag ervoor ontvang je per e-mail een tijdsblok van 2 uur.</>
          }
        />

        <AdresRegel
          label={isPickup ? 'Ophaaladres' : 'Afleveradres'}
          adres={isPickup ? order.ophaalAdres : order.afleverAdres}
          onWijzig={onRefresh && !showAdresEdit ? handleOpenAdresEdit : undefined}
        />

        {/* Bevestiging na adreswijziging */}
        {adresGewijzigd && !showAdresEdit && (
          <p className="-mt-md mb-lg text-[13px] text-secondary font-medium">
            Adres gewijzigd — de beschikbare momenten hieronder zijn bijgewerkt.
          </p>
        )}

        {/* Adres wijzigen — herbruikbaar gegevens-controleren blok (adres only) */}
        {showAdresEdit && (
          <GegevensControleren
            token={token}
            kant={isPickup ? 'pickup' : 'delivery'}
            huidigAdres={isPickup ? order.ophaalAdres : order.afleverAdres}
            regioHint
            onOpgeslagen={handleAdresOpgeslagen}
            onAnnuleer={() => setShowAdresEdit(false)}
          />
        )}

        {/* PRIMAIRE TAAK-KAART: kies een moment */}
        <Card className="mb-md">
          <p className="section-label mb-md">Kies een moment</p>
          <div className="space-y-sm">
            {alleOpties.map((optie) => {
              const isSelected = selectedRouteId === optie.routeId
              return (
                <button
                  key={optie.id ?? optie.routeId}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSelect(optie)}
                  className={`route-option w-full text-left ${isSelected ? 'selected' : ''} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${isSelected ? 'border-primary bg-primary' : 'border-track'}
                  `}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body text-ink truncate">{optie.label}</span>
                      {/* Opslagkosten badge - alleen tonen bij afleveren */}
                      {!isPickup && order.opslagkostenStartDatum && (
                        <OpslagkostenBadge
                          startDatum={order.opslagkostenStartDatum}
                          afleverDatum={optie.datum}
                          compact
                          className="flex-shrink-0"
                        />
                      )}
                    </div>
                    {optie.capaciteit && optie.capaciteit <= 3 && (
                      <span className="text-body-sm text-amber-600">
                        Nog {optie.capaciteit} {optie.capaciteit === 1 ? 'plek' : 'plekken'}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {onLaadMeerOpties && (!extraLoaded || hasMoreExtra) && (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLading || isSubmitting}
              className="w-full mt-sm text-center text-primary hover:text-primary-dark text-body-sm font-medium py-sm disabled:opacity-50"
            >
              {isLading ? 'Laden...' : 'Toon meer datums'}
            </button>
          )}

          {/* Uitleg bij de momenten: regio-routes + opslagkosten (alleen afleveren) */}
          <div className="mt-md pt-md border-t border-hairline space-y-xs">
            <p className="text-[13px] text-ink-faint">
              Dit zijn de dagen dat we bij jou in de regio rijden.
            </p>
            {!isPickup && order.opslagkostenStartDatum && (
              <p className="text-[13px] text-ink-faint">
                {KLEDA_CLAIMS.opslag}
              </p>
            )}
          </div>
        </Card>

        {/* Instructies — vóór het bevestigen, gaan mee met de afspraak */}
        <ActionList className="mb-md">
          <ActionRow
            label={isPickup ? 'Instructies voor de chauffeur' : 'Instructies voor de bezorging'}
            actionLabel={showOpmerkingen ? 'Sluiten' : opmerkingen ? 'Bewerken' : 'Toevoegen'}
            onClick={() => setShowOpmerkingen(v => !v)}
          />
        </ActionList>

        {/* Instructies-veld — klapt open vanuit de rij, gaat mee met de bevestiging */}
        {showOpmerkingen && (
          <Card className="mb-md">
            <OpmerkingenVeld
              value={opmerkingen}
              onChange={setOpmerkingen}
              placeholder={isPickup
                ? "Bijv. 'Bel bij aankomst' of 'Kleed ligt in garage'"
                : "Bijv. 'Graag voor 14:00' of 'Kleed in woonkamer leggen'"
              }
              disabled={isSubmitting}
            />
          </Card>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body flex items-start gap-sm">
            <Icon name="alert" className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit knop met selectie preview */}
        <div className="space-y-sm mb-md">
          {selectedOptie && (
            <div className="bg-primary/5 rounded-kleda-lg px-md py-sm">
              <p className="text-body-sm text-ink-muted">Gekozen moment:</p>
              <p className="text-body font-medium text-primary-dark">{selectedOptie.label}</p>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!selectedRouteId || isSubmitting}
            className="btn-primary w-full py-md text-body font-medium"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-sm">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Bevestigen...
              </span>
            ) : (
              `${isPickup ? 'Ophaalafspraak' : 'Afleverafspraak'} bevestigen`
            )}
          </button>

          {!selectedRouteId && !isSubmitting && (
            <p className="text-center text-[13px] text-ink-faint">
              Kies eerst een moment hierboven
            </p>
          )}
        </div>

        {/* Vlekkenbaas alleen bij e-mail-deeplink (?highlight=vlekkenbaas);
            het hoofdmoment is de post-bevestiging-stap */}
        {!isPickup && onVlekkenbaasSuccess && vlekkenbaasHighlighted && (
          <div className="mb-md">
            <VlekkenbaasUpsell
              order={order}
              token={token}
              highlighted={vlekkenbaasHighlighted}
              onSuccess={onVlekkenbaasSuccess}
            />
          </div>
        )}

        {/* Secundaire acties als stille rijen */}
        {isPickup && (
          <ActionList className="mb-md">
            <ActionRow
              label="Vloerkleed toevoegen"
              actionLabel="Toevoegen"
              onClick={handleOpenVloerkleedModal}
            />
          </ActionList>
        )}

        {/* Bij afleveren is het kleed al schoon: dit is het scherm waarop de
            klant actief kiest, en tot nu toe het enige zonder foto-ingang */}
        {isAfleverFase(order) && (
          <ActionList className="mb-md">
            <FotoRij />
            <VoorinspectieRij order={order} />
          </ActionList>
        )}

        {/* Veelgestelde vragen — vangt de top-belredenen per fase af */}
        <FaqList items={isPickup ? KLEDA_FAQ.ophaal : KLEDA_FAQ.aflever} className="mb-md" />

        <TrustLine className="px-xs" />

        {/* Vloerkleed toevoegen modal */}
        <VloerkleedToevoegenModal
          isOpen={showVloerkleedModal}
          onClose={() => setShowVloerkleedModal(false)}
          pricing={pricing}
          isLoadingPricing={isLoadingPricing}
          onAddVloerkleed={handleAddVloerkleed}
          token={token}
        />
      </div>
    </PullToRefresh>
  )
}

// =============================================================================
// AFSPRAAK BEVESTIGD VIEW — success-stap op App-niveau
// Leeft bewust BUITEN de status-switch: na bevestigen springt de order-status
// direct naar 'ingepland' (refetch), maar deze stap blijft staan tot de klant
// zelf op "Verder" klikt. Bij afleveren is dit hét Vlekkenbaas-moment.
// =============================================================================

interface AfspraakBevestigdViewProps {
  order: OrderData
  token: string
  moment: string | null
  isPickup: boolean
  onVerder: () => void
  onVlekkenbaasSuccess?: () => void
}

export function AfspraakBevestigdView({ order, token, moment, isPickup, onVerder, onVlekkenbaasSuccess }: AfspraakBevestigdViewProps) {
  const itemCount = order.items?.filter(i => i.productCategory === 'Primary').length || 0
  const itemTekst = itemCount === 1 ? 'vloerkleed' : 'vloerkleden'

  // Scroll naar boven: de klant stond op de vorige pagina bij de bevestigknop
  // (onderaan) en moet hier eerst de ✓-bevestiging zien
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  // Na toevoegen: korte ✓-bevestiging tonen en dan automatisch door naar de
  // gepland-pagina — de keuze is gemaakt, er valt hier niets meer te doen
  const handleVlekkenbaasToegevoegd = useCallback(() => {
    onVlekkenbaasSuccess?.()
    window.setTimeout(onVerder, 1500)
  }, [onVlekkenbaasSuccess, onVerder])

  // Afleveren: compacte bevestigings-strip + volle aandacht voor de upsell
  // (details + betalen komen op de volgende pagina). Geen sidebar — dit is
  // een gefocuste tussenstap, zoals de ondertapijt-pagina.
  //
  // Alleen als er ook echt iets te bieden valt: zit de Vlekkenbaas al in de
  // order, dan hoort deze tussenstap overgeslagen te worden. App.tsx houdt
  // dezelfde grens aan en zet de stap dan niet eens aan; deze check is het
  // vangnet voor andere aanroepers.
  if (!isPickup && onVlekkenbaasSuccess && vlekkenbaasTeBieden(order)) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <div className="max-w-lg mx-auto">
          {/* Bevestiging als strip — genoeg om te weten dat het gelukt is */}
          <div className="flex items-center gap-md bg-white rounded-premium border border-track shadow-card px-lg py-md mb-lg">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10 text-secondary flex-shrink-0">
              <Icon name="check" className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <p className="text-body font-semibold text-ink">Afleverafspraak bevestigd!</p>
              <p className="text-body-sm text-ink-muted truncate">
                {moment || 'Binnenkort'} · je ontvangt een bevestiging per e-mail
              </p>
            </div>
          </div>

          {/* Het Vlekkenbaas-moment — onverdeelde aandacht */}
          <p className="section-label mb-sm">Nog iets meegeven voor onderweg?</p>
          <VlekkenbaasUpsell
            order={order}
            token={token}
            forceShow
            onSuccess={handleVlekkenbaasToegevoegd}
          />

          <div className="flex justify-center mt-lg">
            <button
              type="button"
              onClick={onVerder}
              className="btn-outline px-xl py-sm text-body font-medium"
            >
              Nee bedankt, verder →
            </button>
          </div>
        </div>
      </PageLayout>
    )
  }

  // Geen upsell-moment, dus de volledige bevestiging ís de inhoud. Kopij volgt
  // de fase: dezelfde tak bedient ook een aflevering zonder aanbod, en die mag
  // niet "Ophaalafspraak" heten.
  return (
    <PageLayout orderNummer={order.werkOrderNummer}>
      <div className="max-w-lg mx-auto">
        <SuccessConfirmation
          title={isPickup ? 'Ophaalafspraak bevestigd!' : 'Afleverafspraak bevestigd!'}
          subtitle={
            isPickup
              ? `Je ${itemTekst} ${itemCount === 1 ? 'wordt' : 'worden'} opgehaald op het gekozen moment.`
              : `Je ${itemTekst} ${itemCount === 1 ? 'wordt' : 'worden'} bezorgd op het gekozen moment.`
          }
          details={[
            {
              label: isPickup ? 'Ophaalmoment' : 'Aflevermoment',
              value: moment || 'Binnenkort',
            },
            {
              label: isPickup ? 'Ophaaladres' : 'Afleveradres',
              value: cleanAdres(isPickup ? order.ophaalAdres : order.afleverAdres),
            },
            {
              label: 'Aantal kleden',
              value: `${itemCount} ${itemTekst}`,
            },
          ]}
        />

        {/* Bij een aflevering is het kleed schoon en ligt het resultaat klaar */}
        {isAfleverFase(order) && (
          <ActionList className="mt-lg">
            <FotoRij />
          </ActionList>
        )}

        <div className="flex justify-center mt-lg">
          <button
            type="button"
            onClick={onVerder}
            className="btn-outline px-xl py-sm text-body font-medium"
          >
            Verder →
          </button>
        </div>
      </div>
    </PageLayout>
  )
}

// =============================================================================
// AL GEKOZEN VIEW (afspraak al gekozen, wacht op ophalen)
// =============================================================================

interface AlGekozenViewProps {
  order: OrderData
  token: string
  onRefresh?: () => void
}

export function AlGekozenView({ order, token, onRefresh }: AlGekozenViewProps) {
  // Vloerkleed toevoegen state
  const [showVloerkleedModal, setShowVloerkleedModal] = useState(false)
  const [pricing, setPricing] = useState<PricingBootstrap | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(false)

  // Laad pricing wanneer modal opent
  const handleOpenVloerkleedModal = useCallback(async () => {
    setShowVloerkleedModal(true)
    if (!pricing) {
      setIsLoadingPricing(true)
      try {
        const result = await fetchPricingBootstrap(token)
        setPricing(result)
      } catch {
        // Error handled in modal
      } finally {
        setIsLoadingPricing(false)
      }
    }
  }, [token, pricing])

  const handleAddVloerkleed = useCallback(async (request: Omit<AddVloerkleedRequest, 'token'>): Promise<boolean> => {
    const result = await addVloerkleed({ ...request, token })
    if (result.success) {
      if (onRefresh) {
        onRefresh()
      }
      setPricing(prev => prev ? {
        ...prev,
        bestaandeAantalKleden: prev.bestaandeAantalKleden + 1,
        bestaandeTotaalM2: result.nieuwTotaalM2 || prev.bestaandeTotaalM2
      } : null)
      return true
    } else {
      throw new Error(result.message || 'Er ging iets mis')
    }
  }, [token, onRefresh])

  const ingeplandMoment =
    order.ophaalAfspraak?.label ?? order.ophaalMomentDefinitief ?? order.ophaalMomentGekozen ?? null

  return (
    <div>
      <StatusHeader
        title="We komen eraan."
        subtitle={
          <>
            We halen je vloerkleed op het gekozen moment op. Je ontvangt vooraf
            een herinnering — zorg dat het kleed klaarligt.
          </>
        }
      />

      <AdresRegel label="Ophaaladres" adres={order.ophaalAdres} />

      {/* PRIMAIRE TAAK-KAART: het gekozen moment. Bron is de afspraak zelf,
          niet Route_Keuze (dat is een keuzenummer, geen moment). */}
      {ingeplandMoment ? (
        <Card className="mb-md">
          <AfspraakInfo
            label="Ophaalmoment"
            moment={ingeplandMoment}
            isDefinitief={false}
          />
        </Card>
      ) : null}

      {/* Secundaire acties */}
      <ActionList className="mb-md">
        <ActionRow
          label="Vloerkleed toevoegen"
          actionLabel="Toevoegen"
          onClick={handleOpenVloerkleedModal}
        />
        {/* De kop van deze view is ophaal-taal, maar de status kan ook een
            gekozen aflevering zijn; dán is er beeld en hoort de ingang erbij */}
        {isAfleverFase(order) && <FotoRij />}
      </ActionList>

      <TrustLine className="px-xs" />

      {/* Vloerkleed toevoegen modal */}
      <VloerkleedToevoegenModal
        isOpen={showVloerkleedModal}
        onClose={() => setShowVloerkleedModal(false)}
        pricing={pricing}
        isLoadingPricing={isLoadingPricing}
        onAddVloerkleed={handleAddVloerkleed}
        token={token}
      />
    </div>
  )
}

// =============================================================================
// GEPLAND VIEW (definitief ingepland)
// =============================================================================

interface GeplandViewProps {
  order: OrderData
  token: string
  onWijzigAfspraak: (routeId: string, opmerkingen?: string) => Promise<void>
  onAnnuleer: (reden?: string) => Promise<AnnuleerResponseData | null>
  onLaadMeerOpties?: () => Promise<void>
  onSaveOpmerkingen?: (opmerkingen: string, verwacht: string | null) => Promise<void>
  isSavingOpmerkingen?: boolean
  onRefresh?: () => void
  vlekkenbaasHighlighted?: boolean
  onVlekkenbaasSuccess?: () => void
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
}

export function GeplandView({ order, token, onWijzigAfspraak, onAnnuleer, onLaadMeerOpties, onSaveOpmerkingen, isSavingOpmerkingen, onRefresh, onPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut }: GeplandViewProps) {
  const [showWijzigModal, setShowWijzigModal] = useState(false)
  const [showAnnuleerModal, setShowAnnuleerModal] = useState(false)
  const [showOpmerkingenEdit, setShowOpmerkingenEdit] = useState(false)
  const [showGegevensEdit, setShowGegevensEdit] = useState(false)

  // Vloerkleed toevoegen state
  const [showVloerkleedModal, setShowVloerkleedModal] = useState(false)
  const [pricing, setPricing] = useState<PricingBootstrap | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(false)

  const isPickup = order.phaseType === 'pickup'
  const huidigeOpmerkingen = isPickup ? order.opmerkingenOphalen : order.opmerkingenAfleveren
  const [opmerkingenText, setOpmerkingenText] = useState(huidigeOpmerkingen || '')
  const [opmerkingenBasis, setOpmerkingenBasis] = useState(huidigeOpmerkingen ?? null)
  const [opmerkingenSaved, setOpmerkingenSaved] = useState(false)
  const [opmerkingenError, setOpmerkingenError] = useState<string | null>(null)

  const handleSaveOpmerkingen = async () => {
    if (!onSaveOpmerkingen) return
    setOpmerkingenError(null)
    try {
      await onSaveOpmerkingen(opmerkingenText, opmerkingenBasis)
      setShowOpmerkingenEdit(false)
      setOpmerkingenSaved(true)
      setTimeout(() => setOpmerkingenSaved(false), 3000)
    } catch (error) {
      setOpmerkingenError(error instanceof Error ? error.message : 'Opslaan mislukt. Controleer je opmerkingen en probeer opnieuw.')
    }
  }

  // Afspraak als één geheel: label, venster en "definitief"-badge komen uit
  // dezelfde bron (AfspraakDto). De losse velden blijven enkel als terugval
  // voor een oude API-respons — die houden het brede klantvenster vast, ook als
  // er al een smal definitief blok is.
  const afspraak = isPickup ? order.ophaalAfspraak : order.afleverAfspraak
  const momentLabel = afspraak?.label
    ?? (isPickup
      ? order.ophaalMomentDefinitief || order.ophaalMomentGekozen
      : order.afleverMomentDefinitief || order.afleverMomentGekozen)
  const huidigeAfspraak = momentLabel

  // Laad pricing wanneer modal opent
  const handleOpenVloerkleedModal = useCallback(async () => {
    setShowVloerkleedModal(true)
    if (!pricing) {
      setIsLoadingPricing(true)
      try {
        const result = await fetchPricingBootstrap(token)
        setPricing(result)
      } catch {
        // Error handled in modal
      } finally {
        setIsLoadingPricing(false)
      }
    }
  }, [token, pricing])

  const handleAddVloerkleed = useCallback(async (request: Omit<AddVloerkleedRequest, 'token'>): Promise<boolean> => {
    const result = await addVloerkleed({ ...request, token })
    if (result.success) {
      if (onRefresh) {
        onRefresh()
      }
      setPricing(prev => prev ? {
        ...prev,
        bestaandeAantalKleden: prev.bestaandeAantalKleden + 1,
        bestaandeTotaalM2: result.nieuwTotaalM2 || prev.bestaandeTotaalM2
      } : null)
      return true
    } else {
      throw new Error(result.message || 'Er ging iets mis')
    }
  }, [token, onRefresh])

  const enkelKleed = order.items.length === 1
  // Niet op betaalLink gaten: sinds besluit 29-07 ontstaat de link pas in de
  // aflever-fase, en tussen de R4-flip (21-08) en de aflever-poort (14-09)
  // kreeg een deel van de orders er helemaal geen (WB-323454: "waar is de
  // betaalknop?"). Het openstaand bedrag is de waarheid; openBetaalLink mint
  // de link ter plekke als hij ontbreekt. Op factuur = nooit een knop.
  const betalingOpen = !isPickup && !order.opFactuur && (order.openstaandBedrag ?? 0) > 0 && order.betaalStatus !== 'betaald'
  const [geenLink, setGeenLink] = useState(false)
  const afspraakVanaf = afspraak?.vanaf ?? (isPickup ? order.ophalenVanaf : order.afleverenVanaf)
  const afspraakTot = afspraak?.tot ?? (isPickup ? order.ophalenTot : order.afleverenTot)
  // yyyy-mm-dd van de huidige afspraak, om die dag in de wijzig-lijst te
  // markeren in plaats van hem als gewone keuze aan te bieden. Route-opties
  // dragen hun datum in dezelfde vorm.
  const huidigeDatum = afspraakVanaf ? datumSleutel(afspraakVanaf) : undefined

  // Het krappe 2-uurs tijdsblok is pas definitief als het er echt is (dag
  // ervoor); tot die tijd is de afspraak "bevestigd" met een ruim blok.
  const heeftDefinitief = afspraak?.isDefinitief
    ?? Boolean(isPickup ? order.ophaalMomentDefinitief : order.afleverMomentDefinitief)

  // Aftellen pas tonen als het dichtbij komt — bij een afspraak over weken
  // is een tikkende teller ruis. Daarvoor volstaat een stille regel.
  const msTeGaan = afspraakVanaf ? new Date(afspraakVanaf).getTime() - Date.now() : null
  const dagenTeGaan = msTeGaan != null ? Math.ceil(msTeGaan / 86400000) : null

  // Binnen 24 uur juist géén teller: het blok is twee uur breed, dus een klok
  // die seconden wegtikt suggereert een aankomsttijd die we niet toezeggen.
  // Dan alleen de stand van zaken.
  const isBinnen24Uur = msTeGaan != null && msTeGaan < 86400000
  const toonTimer = dagenTeGaan != null && dagenTeGaan <= 7 && !isBinnen24Uur

  // Intro = geruststelling + wat de klant nog moet doen (niets, behalve
  // klaarleggen op de dag zelf). De datum leeft in de afspraak-kaart eronder.
  // "Hoe laat komen jullie?" en "belt de chauffeur?" zijn de twee grootste
  // belredenen in deze fase (inbox-analyse). Dus: wanneer het blok komt, en
  // dat er niet gebeld wordt, in één adem.
  const tijdsblokZin = heeftDefinitief
    ? 'Het tijdsblok hieronder is definitief; de chauffeur belt niet vooraf.'
    : 'De dag ervoor ontvang je per e-mail een tijdsblok van 2 uur. De chauffeur belt niet vooraf.'
  const intro = isPickup ? (
    <>
      Je hoeft verder niets te doen. Leg {enkelKleed ? 'je vloerkleed' : 'je vloerkleden'} op
      de ophaaldag opgerold klaar. {tijdsblokZin} Een rol, banden of etiketten die je wilt houden
      haal je er vooraf af; die komen niet gegarandeerd mee terug.
    </>
  ) : (
    <>
      {enkelKleed ? 'Je schone vloerkleed komt' : 'Je schone vloerkleden komen'} eraan. {tijdsblokZin}
    </>
  )

  // Binnen 24 uur en nog geen krap blok: dat is precies het moment waarop
  // klanten bellen. Zeg wat er gaat gebeuren en wat ze kunnen doen.
  const blokOnderweg = isBinnen24Uur && !heeftDefinitief && msTeGaan != null && msTeGaan > 0

  return (
    <div>
      <StatusHeader
        title={isPickup ? 'We komen eraan.' : 'Bijna weer thuis.'}
        subtitle={intro}
      />

      <AdresRegel
        label={isPickup ? 'Ophaaladres' : 'Afleveradres'}
        adres={isPickup ? order.ophaalAdres : order.afleverAdres}
      />

      {blokOnderweg && (
        <div className="mb-md rounded-kleda-md border border-chip-amber-line bg-chip-amber px-md py-sm text-body-sm text-chip-amber-ink">
          Je tijdsblok van 2 uur is nog niet vastgesteld. Zodra de route rond is, krijg je het per e-mail;
          houd anders rekening met {afspraakVanaf && afspraakTot ? `${formatTijd(afspraakVanaf)} tot ${formatTijd(afspraakTot)}` : 'het ruime tijdvak hieronder'}.
          Niets ontvangen op de dag zelf? Bel {KLEDA_CONTACT.telefoonWeergave}.
        </div>
      )}

      {/* PRIMAIRE TAAK-KAART
          Betaling open → betaalkaart als kalm middelpunt (3 states: polling /
          timeout / openstaand). Anders → afspraak + countdown. */}
      {betalingOpen ? (
        isPaymentPolling ? (
          <Card className="mb-md">
            <div className="flex items-center gap-sm">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <div>
                <p className="text-body font-semibold text-ink">Betaling wordt verwerkt...</p>
                <p className="text-body-sm text-ink-muted">
                  Dit duurt meestal een paar seconden.
                </p>
              </div>
            </div>
          </Card>
        ) : paymentTimedOut ? (
          <Card className="mb-md">
            <div className="flex items-start gap-sm">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 flex-shrink-0">
                <Icon name="check" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-body font-semibold text-ink">Betaling wordt nog verwerkt</p>
                <p className="text-body-sm text-ink-muted">
                  Heb je net betaald? Dan is je betaling bij ons binnen, maar is de bevestiging nog onderweg. Ververs de pagina over een paar minuten.
                </p>
                <div className="mt-sm flex items-center gap-md text-body-sm">
                  <button
                    type="button"
                    onClick={() => onRefresh?.()}
                    className="text-primary-dark font-medium underline hover:text-primary"
                  >
                    Ververs nu
                  </button>
                  <button
                    type="button"
                    onClick={() => resetPaymentTimedOut?.()}
                    className="text-ink-faint hover:text-ink-soft"
                  >
                    Klopt dit niet? Probeer opnieuw
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="mb-md">
            <p className="section-label mb-xs">Nog te betalen</p>
            {order.openstaandBedrag != null && (
              <p className="font-heading font-bold text-[34px] leading-tight text-ink mb-md">
                {formatEUR(order.openstaandBedrag)}
              </p>
            )}
            <button
              type="button"
              onClick={async () => {
                // Link vers ophalen op het betaalmoment: kosten die ná het
                // laden bijkwamen (opslag bij datumkeuze, upsell) betalen mee.
                const uitkomst = await openBetaalLink(token, order.betaalLink)
                if (uitkomst === 'betaald') {
                  onRefresh?.()
                  return
                }
                if (uitkomst === 'geen-link') {
                  setGeenLink(true)
                  return
                }
                onPaymentInitiated?.()
                trackEvent('payment_initiated', { werkOrderId: order.werkOrderId, amount: order.openstaandBedrag })
              }}
              className="btn-primary px-xl py-sm text-[15px] font-bold"
            >
              Betaal via iDEAL
            </button>
            <p className="mt-md text-[13px] text-ink-faint">
              {geenLink
                ? 'Online betalen lukt nu even niet. Je kunt bij aflevering betalen met pin of contant (gepast) aan de deur.'
                : 'Liever bij aflevering? Pin of contant (gepast) aan de deur.'}
            </p>
          </Card>
        )
      ) : null}

      {/* Afspraak-details — zichtbaar zodra er een moment is, ook als betalen
          de hoofdtaak is. Zonder moment géén kaart: AfspraakInfo rendert dan
          null en de Card bleef als leeg wit blok staan op precies de plek waar
          de datum hoort (09-09, klasse "convert vult de WO-afspraak niet"). */}
      {huidigeAfspraak ? (
      <Card className="mb-md">
        <div className={afspraakVanaf && (toonTimer || isBinnen24Uur) ? 'grid grid-cols-1 md:grid-cols-2 gap-lg' : ''}>
          {/* Afspraak info */}
          <AfspraakInfo
            label={isPickup ? "Ophaalmoment" : "Aflevermoment"}
            moment={huidigeAfspraak}
            isDefinitief={heeftDefinitief}
          />

          {/* Aftellen — alleen in de dagen ervoor, niet op de dag zelf */}
          {toonTimer && afspraakVanaf && (
            <div className="md:border-l md:border-hairline md:pl-lg">
              <p className="section-label mb-sm">Nog even</p>
              <CountdownTimer targetDate={new Date(afspraakVanaf)} />
            </div>
          )}

          {/* Binnen 24 uur: stand van zaken zonder teller */}
          {isBinnen24Uur && afspraakVanaf && (
            <div className="md:border-l md:border-hairline md:pl-lg">
              <p className="section-label mb-sm">Stand van zaken</p>
              <AfspraakStatus vanaf={afspraakVanaf} tot={afspraakTot} />
            </div>
          )}
        </div>

        {/* Ver weg: stille regel in plaats van tikkende teller */}
        {!toonTimer && dagenTeGaan != null && dagenTeGaan > 7 && (
          <p className="mt-md pt-md border-t border-hairline text-[13px] text-ink-faint">
            {dagenTeGaan >= 14
              ? `Over ${Math.round(dagenTeGaan / 7)} weken — wil je het eerder? Je kunt je afspraak hieronder wijzigen.`
              : `Over ${dagenTeGaan} dagen — wil je het eerder? Je kunt je afspraak hieronder wijzigen.`}
          </p>
        )}
      </Card>
      ) : order.kanWijzigen ? (
        /* Geen moment bekend maar wel te wijzigen: zeg dat, in plaats van een
           leeg vak. De klant weet dan waar hij aan toe is en heeft een uitweg. */
        <Card className="mb-md">
          <p className="section-label mb-xs">{isPickup ? 'Ophaalmoment' : 'Aflevermoment'}</p>
          <p className="text-body-sm text-ink-muted">
            Je afspraak staat gepland, maar we kunnen de datum hier even niet
            laten zien. Bel {KLEDA_CONTACT.telefoonWeergave} en we zoeken het
            direct voor je op.
          </p>
        </Card>
      ) : null}

      {/* Volgende reiniging in de aflever-fase (plan 2026-10-26, besluit 3):
          pas als er niets meer te betalen is; betalen gaat voor (één
          secundair element per pagina). */}
      {TERUGKOM_KAART_AAN && token && isAfleverFase(order) && !betalingOpen && (
        <VolgendeKeerKaart token={token} werkOrderId={order.werkOrderId} heeftMobiel={!!order.mobiel} bron="aflever-fase" className="mb-md" />
      )}

      {/* Besteloverzicht — mobile only (desktop toont in sidebar) */}
      {!isPickup && (
        <div className="mb-md lg:hidden">
          <OrderSummary
            order={order}
            token={token}
            onRefresh={onRefresh}
            onPaymentInitiated={onPaymentInitiated}
            isPaymentPolling={isPaymentPolling}
            paymentTimedOut={paymentTimedOut}
            resetPaymentTimedOut={resetPaymentTimedOut}
          />
        </div>
      )}

      {/* Secundaire acties als stille rijen */}
      <ActionList className="mb-md">
        {order.kanWijzigen && (
          <ActionRow
            label={isPickup ? 'Ophaalmoment wijzigen' : 'Aflevermoment wijzigen'}
            actionLabel="Wijzigen"
            onClick={() => setShowWijzigModal(true)}
          />
        )}
        {onSaveOpmerkingen && (
          <ActionRow
            label="Instructies voor de chauffeur"
            sublabel={huidigeOpmerkingen
              ? `"${huidigeOpmerkingen}"${opmerkingenSaved ? ' · Opgeslagen ✓' : ''}`
              : undefined}
            actionLabel={showOpmerkingenEdit ? 'Sluiten' : huidigeOpmerkingen ? 'Bewerken' : 'Toevoegen'}
            onClick={() => {
              setOpmerkingenText(huidigeOpmerkingen || '')
              setOpmerkingenBasis(huidigeOpmerkingen ?? null)
              setOpmerkingenError(null)
              setShowOpmerkingenEdit(v => !v)
            }}
          />
        )}
        {order.kanGegevensWijzigen && (
          <ActionRow
            label="Adres en telefoonnummer controleren"
            sublabel={cleanAdres(isPickup ? order.ophaalAdres : order.afleverAdres) +
              (order.mobiel ? ` · ${order.mobiel}` : '')}
            actionLabel={showGegevensEdit ? 'Sluiten' : 'Controleren'}
            onClick={() => setShowGegevensEdit(v => !v)}
          />
        )}
        {isAfleverFase(order) && <FotoRij />}
        {isAfleverFase(order) && <VoorinspectieRij order={order} />}
        {isPickup && (
          <ActionRow
            label="Vloerkleed toevoegen"
            actionLabel="Toevoegen"
            onClick={handleOpenVloerkleedModal}
          />
        )}
        {order.kanAnnuleren && (
          <ActionRow
            label="Bestelling annuleren"
            actionLabel="Annuleren"
            danger
            onClick={() => setShowAnnuleerModal(true)}
          />
        )}
      </ActionList>

      {/* Adres + telefoon controleren — klapt open vanuit de rij */}
      {showGegevensEdit && (
        <GegevensControleren
          token={token}
          kant={isPickup ? 'pickup' : 'delivery'}
          huidigAdres={isPickup ? order.ophaalAdres : order.afleverAdres}
          huidigMobiel={order.mobiel}
          toonTelefoon
          routeGelockt={order.routeGelockt}
          onOpgeslagen={() => {
            setShowGegevensEdit(false)
            onRefresh?.()
          }}
          onAnnuleer={() => setShowGegevensEdit(false)}
        />
      )}

      {/* Instructies-editor — klapt open vanuit de rij */}
      {showOpmerkingenEdit && (
        <Card className="mb-md">
          <p className="section-label mb-sm">
            {isPickup ? 'Instructies voor ophalen' : 'Instructies voor afleveren'}
          </p>
          <p className="text-body-sm text-ink-muted mb-sm">
            {isPickup
              ? 'Laat de chauffeur weten waar het kleed ligt of hoe hij je kan bereiken.'
              : 'Laat de chauffeur weten waar hij het kleed mag neerleggen.'}
          </p>
          <textarea
            value={opmerkingenText}
            onChange={(e) => setOpmerkingenText(e.target.value)}
            placeholder={isPickup
              ? "Bijv. 'Kleed ligt in de garage' of 'Bel aan, niet kloppen'"
              : "Bijv. 'Graag in de woonkamer leggen' of 'Bel aan, niet kloppen'"
            }
            disabled={isSavingOpmerkingen}
            rows={2}
            className="textarea w-full"
          />
          {opmerkingenError && <p role="alert" className="text-body-sm text-red-700 mt-sm">{opmerkingenError}</p>}
          <div className="flex gap-sm mt-sm">
            <button
              onClick={handleSaveOpmerkingen}
              disabled={isSavingOpmerkingen || !onSaveOpmerkingen}
              className="btn-primary flex-1 py-xs text-body-sm font-medium"
            >
              {isSavingOpmerkingen ? 'Opslaan...' : 'Opslaan'}
            </button>
            <button
              onClick={() => {
                setShowOpmerkingenEdit(false)
                setOpmerkingenText(huidigeOpmerkingen || '')
              }}
              className="btn-secondary py-xs text-body-sm"
            >
              Annuleren
            </button>
          </div>
        </Card>
      )}

      {/* Veelgestelde vragen — per fase */}
      <FaqList items={isPickup ? KLEDA_FAQ.ophaal : KLEDA_FAQ.aflever} className="mb-md" />

      <TrustLine className="px-xs" />

      {/* Wijzig Modal */}
      <WijzigAfspraakModal
        isOpen={showWijzigModal}
        onClose={() => setShowWijzigModal(false)}
        routeOpties={order.routeOpties}
        extraRouteOpties={order.extraRouteOpties}
        huidigeAfspraak={huidigeAfspraak}
        huidigeDatum={huidigeDatum}
        fase={isPickup ? 'pickup' : 'delivery'}
        kosten={order.wijzigKosten}
        // Alleen bij afleveren: opslag loopt niet tijdens de ophaalfase.
        opslagkostenStartDatum={isPickup ? undefined : order.opslagkostenStartDatum}
        onWijzig={onWijzigAfspraak}
        onLaadMeerOpties={onLaadMeerOpties}
      />

      {/* Annuleer Modal */}
      <AnnulerenModal
        isOpen={showAnnuleerModal}
        onClose={() => setShowAnnuleerModal(false)}
        kosten={order.wijzigKosten}
        onAnnuleer={onAnnuleer}
        onComplete={onRefresh}
      />

      {/* Vloerkleed toevoegen modal */}
      <VloerkleedToevoegenModal
        isOpen={showVloerkleedModal}
        onClose={() => setShowVloerkleedModal(false)}
        pricing={pricing}
        isLoadingPricing={isLoadingPricing}
        onAddVloerkleed={handleAddVloerkleed}
        token={token}
      />
    </div>
  )
}

// =============================================================================
// ZELF-HALEN-EN-BRENGEN-PAD
// De klant brengt het kleed zelf naar Havelte en/of haalt het daar zelf op.
// Geen route, geen tijdsblok, geen chauffeur: de taak is "kom langs binnen
// de openingstijden". Copy spiegelt de ZS01/ZS02-mails uit de comms-motor,
// zodat mail en portal hetzelfde zeggen.
// =============================================================================

const WASSERIJ_ROUTE_URL = 'https://maps.google.com/?q=Oeveraseweg+13a,+7971PA+Havelte'

/** Adres, openingstijden en routeknop van de wasserij, één kaart voor beide zelf-views */
function WasserijLocatieKaart({ label, werkOrderNummer }: { label: string; werkOrderNummer: string }) {
  return (
    <Card className="mb-md">
      <p className="section-label mb-xs">{label}</p>
      <p className="text-body font-semibold text-ink">Kleda Wasserij</p>
      <p className="text-body-sm text-ink-muted mb-md">{KLEDA_CONTACT.adres}</p>

      <p className="section-label mb-xs">Openingstijden</p>
      <p className="text-body-sm text-ink-muted mb-md">{KLEDA_CONTACT.klantenservice}</p>

      <a
        href={WASSERIJ_ROUTE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary inline-block px-xl py-sm text-[15px] font-bold"
      >
        Bekijk route
      </a>

      <p className="mt-md pt-md border-t border-hairline text-[13px] text-ink-faint">
        Meenemen: je ordernummer <span className="font-medium text-ink-soft">{werkOrderNummer}</span>.
      </p>
    </Card>
  )
}

interface ZelfBrengenViewProps {
  order: OrderData
  onSaveOpmerkingen?: (opmerkingen: string, verwacht: string | null) => Promise<void>
  isSavingOpmerkingen?: boolean
}

export function ZelfBrengenView({ order, onSaveOpmerkingen, isSavingOpmerkingen }: ZelfBrengenViewProps) {
  const enkelKleed = order.items.length === 1
  const huidigeOpmerkingen = order.opmerkingenOphalen
  const [showOpmerkingenEdit, setShowOpmerkingenEdit] = useState(false)
  const [opmerkingenText, setOpmerkingenText] = useState(huidigeOpmerkingen || '')
  const [opmerkingenBasis, setOpmerkingenBasis] = useState(huidigeOpmerkingen ?? null)
  const [opmerkingenSaved, setOpmerkingenSaved] = useState(false)
  const [opmerkingenError, setOpmerkingenError] = useState<string | null>(null)

  const handleSaveOpmerkingen = async () => {
    if (!onSaveOpmerkingen) return
    setOpmerkingenError(null)
    try {
      await onSaveOpmerkingen(opmerkingenText, opmerkingenBasis)
      setShowOpmerkingenEdit(false)
      setOpmerkingenSaved(true)
      setTimeout(() => setOpmerkingenSaved(false), 3000)
    } catch (error) {
      setOpmerkingenError(error instanceof Error ? error.message : 'Opslaan mislukt. Controleer je opmerkingen en probeer opnieuw.')
    }
  }

  return (
    <div>
      <StatusHeader
        title="Breng je vloerkleed wanneer het jou uitkomt."
        subtitle={
          <>
            Je brengt {enkelKleed ? 'je vloerkleed' : 'je vloerkleden'} zelf naar onze wasserij in Havelte.
            Een afspraak is niet nodig, kom langs binnen de openingstijden.
          </>
        }
      />

      <WasserijLocatieKaart label="Onze locatie" werkOrderNummer={order.werkOrderNummer} />

      {/* Wat de klant kan verwachten, uit ZS01 */}
      <Card className="mb-md">
        <p className="section-label mb-sm">Goed om te weten</p>
        <ul className="space-y-xs text-body-sm text-ink-muted">
          <li>Bij binnenkomst nemen we {enkelKleed ? 'je vloerkleed' : 'je vloerkleden'} in ontvangst en doen we een voorinspectie. Je ontvangt een bevestiging van de afmetingen en de werkelijke kosten.</li>
          <li>{KLEDA_CLAIMS.doorlooptijd}</li>
          <li>Zodra {enkelKleed ? 'het klaar is' : 'ze klaar zijn'} ontvang je een e-mail. {order.zelfHalen ? 'Je haalt het dan zelf weer op bij de wasserij.' : 'Je kiest dan een aflevermoment.'}</li>
          <li>Heb je specifieke wensen voor de behandeling? Geef dat bij het brengen aan, of zet het hieronder bij je opmerkingen.</li>
        </ul>
      </Card>

      {/* Secundaire acties. Geen annuleer- of wijzigknop: dat venster staat
          server-side dicht op deze status; bellen kan altijd. */}
      {onSaveOpmerkingen && (
        <ActionList className="mb-md">
          <ActionRow
            label="Opmerkingen voor de wasserij"
            sublabel={huidigeOpmerkingen
              ? `"${huidigeOpmerkingen}"${opmerkingenSaved ? ' · Opgeslagen ✓' : ''}`
              : undefined}
            actionLabel={showOpmerkingenEdit ? 'Sluiten' : huidigeOpmerkingen ? 'Bewerken' : 'Toevoegen'}
            onClick={() => {
              setOpmerkingenText(huidigeOpmerkingen || '')
              setOpmerkingenBasis(huidigeOpmerkingen ?? null)
              setOpmerkingenError(null)
              setShowOpmerkingenEdit(v => !v)
            }}
          />
        </ActionList>
      )}

      {showOpmerkingenEdit && (
        <Card className="mb-md">
          <p className="section-label mb-sm">Opmerkingen voor de wasserij</p>
          <p className="text-body-sm text-ink-muted mb-sm">
            Bijzonderheden over vlekken, materiaal of wensen voor de behandeling.
          </p>
          <textarea
            value={opmerkingenText}
            onChange={(e) => setOpmerkingenText(e.target.value)}
            placeholder="Bijv. 'Koffievlek rechtsonder' of 'Graag extra aandacht voor de franjes'"
            disabled={isSavingOpmerkingen}
            rows={2}
            className="textarea w-full"
          />
          {opmerkingenError && <p role="alert" className="text-body-sm text-red-700 mt-sm">{opmerkingenError}</p>}
          <div className="flex gap-sm mt-sm">
            <button
              onClick={handleSaveOpmerkingen}
              disabled={isSavingOpmerkingen}
              className="btn-primary flex-1 py-xs text-body-sm font-medium"
            >
              {isSavingOpmerkingen ? 'Opslaan...' : 'Opslaan'}
            </button>
            <button
              onClick={() => {
                setShowOpmerkingenEdit(false)
                setOpmerkingenText(huidigeOpmerkingen || '')
              }}
              className="btn-secondary py-xs text-body-sm"
            >
              Annuleren
            </button>
          </div>
        </Card>
      )}

      {/* De ophaal-FAQ gaat over de route; de reinigen-set past hier wel */}
      <FaqList items={KLEDA_FAQ.reinigen} className="mb-md" />

      <TrustLine className="px-xs" />
    </div>
  )
}

interface ZelfHalenViewProps {
  order: OrderData
  token: string
  onRefresh?: () => void
  vlekkenbaasHighlighted?: boolean
  onVlekkenbaasSuccess?: () => void
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
}

export function ZelfHalenView({ order, token, onRefresh, vlekkenbaasHighlighted, onVlekkenbaasSuccess, onPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut }: ZelfHalenViewProps) {
  const enkelKleed = order.items.length === 1
  const [geenLink, setGeenLink] = useState(false)

  // Niet op betaalLink gaten zoals GeplandView: zelf-orders krijgen op het
  // SF-pad geen Mollie-link (die wordt op het route-pad gemint). Het
  // openstaand bedrag is hier leidend; openBetaalLink mint de link ter plekke.
  const betalingOpen = (order.openstaandBedrag ?? 0) > 0 && order.betaalStatus !== 'betaald'

  return (
    <div>
      <StatusHeader
        title={enkelKleed ? 'Je vloerkleed is klaar.' : 'Je vloerkleden zijn klaar.'}
        subtitle={
          <>
            Schoon en klaar om opgehaald te worden bij onze wasserij in Havelte.
            Kom langs binnen de openingstijden, een afspraak is niet nodig.
          </>
        }
      />

      {/* PRIMAIRE TAAK-KAART: betalen als er nog iets openstaat, zelfde drie
          states als GeplandView (polling / timeout / openstaand) */}
      {betalingOpen ? (
        isPaymentPolling ? (
          <Card className="mb-md">
            <div className="flex items-center gap-sm">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <div>
                <p className="text-body font-semibold text-ink">Betaling wordt verwerkt...</p>
                <p className="text-body-sm text-ink-muted">Dit duurt meestal een paar seconden.</p>
              </div>
            </div>
          </Card>
        ) : paymentTimedOut ? (
          <Card className="mb-md">
            <div className="flex items-start gap-sm">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 flex-shrink-0">
                <Icon name="check" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-body font-semibold text-ink">Betaling wordt nog verwerkt</p>
                <p className="text-body-sm text-ink-muted">
                  Heb je net betaald? Dan is je betaling bij ons binnen, maar is de bevestiging nog onderweg. Ververs de pagina over een paar minuten.
                </p>
                <div className="mt-sm flex items-center gap-md text-body-sm">
                  <button type="button" onClick={() => onRefresh?.()} className="text-primary-dark font-medium underline hover:text-primary">
                    Ververs nu
                  </button>
                  <button type="button" onClick={() => resetPaymentTimedOut?.()} className="text-ink-faint hover:text-ink-soft">
                    Klopt dit niet? Probeer opnieuw
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="mb-md">
            <p className="section-label mb-xs">Nog te betalen</p>
            {order.openstaandBedrag != null && (
              <p className="font-heading font-bold text-[34px] leading-tight text-ink mb-md">
                {formatEUR(order.openstaandBedrag)}
              </p>
            )}
            <button
              type="button"
              onClick={async () => {
                const uitkomst = await openBetaalLink(token, order.betaalLink)
                if (uitkomst === 'betaald') {
                  onRefresh?.()
                  return
                }
                if (uitkomst === 'geen-link') {
                  setGeenLink(true)
                  return
                }
                onPaymentInitiated?.()
                trackEvent('payment_initiated', { werkOrderId: order.werkOrderId, amount: order.openstaandBedrag })
              }}
              className="btn-primary px-xl py-sm text-[15px] font-bold"
            >
              Betaal via iDEAL
            </button>
            <p className="mt-md text-[13px] text-ink-faint">
              {geenLink
                ? 'Online betalen lukt nu even niet. Je kunt bij het ophalen betalen met pin of contant (gepast).'
                : 'Liever bij het ophalen? Pin of contant (gepast) aan de balie.'}
            </p>
          </Card>
        )
      ) : null}

      <WasserijLocatieKaart label="Ophaallocatie" werkOrderNummer={order.werkOrderNummer} />

      {/* Vlekkenbaas: hetzelfde moment als in de afleverfase */}
      {vlekkenbaasTeBieden(order) && (
        <div className="mb-md">
          <VlekkenbaasUpsell
            order={order}
            token={token}
            highlighted={vlekkenbaasHighlighted}
            onSuccess={onVlekkenbaasSuccess ?? (() => {})}
          />
        </div>
      )}

      {/* Besteloverzicht op mobiel: PortalLayout verbergt hem in de
          afleverfase omdat de view hem inline toont, dus hier ook */}
      <div className="mb-md lg:hidden">
        <OrderSummary
          order={order}
          token={token}
          onRefresh={onRefresh}
          onPaymentInitiated={onPaymentInitiated}
          isPaymentPolling={isPaymentPolling}
          paymentTimedOut={paymentTimedOut}
          resetPaymentTimedOut={resetPaymentTimedOut}
        />
      </div>

      {/* Het na-foto-moment: hier landt de ZS02-link */}
      <ActionList className="mb-md">
        <FotoRij />
        <VoorinspectieRij order={order} />
      </ActionList>

      <TrustLine className="px-xs" />
    </div>
  )
}

// =============================================================================
// IN BEHANDELING VIEW
// =============================================================================

interface InBehandelingViewProps {
  order: OrderData
}

export function InBehandelingView({ order }: InBehandelingViewProps) {
  const enkelKleed = (order.items?.length || 0) <= 1
  const kleedWoord = enkelKleed ? 'kleed' : 'kleden'

  // Status-specifieke kop: bij 'Opgehaald' ligt het kleed net in de bus —
  // dan is "wordt nu gewassen" voorbarig. Het moment vraagt om een
  // overdracht-bevestiging; de was-kop klopt pas bij 'Aan het reinigen'.
  const wordtGewassen = order.status === 'Aan het reinigen'
  const title = wordtGewassen
    ? `Je ${kleedWoord} ${enkelKleed ? 'wordt' : 'worden'} nu met zorg gewassen.`
    : `Je ${kleedWoord} ${enkelKleed ? 'is' : 'zijn'} veilig onderweg naar onze wasserij.`
  const subtitle = wordtGewassen
    ? <>We laten je weten zodra {enkelKleed ? 'het klaar is' : 'ze klaar zijn'} en je een aflevermoment kunt kiezen.</>
    : <>Eerst doen we een voorinspectie: we meten je {kleedWoord} exact na en bekijken vlekken en bijzonderheden. Daarna gaat {enkelKleed ? 'het' : 'alles'} de wasserij in.</>

  // "Waar blijft mijn kleed?" is de grootste vraag in deze fase. Een
  // datumvenster uit de ophaaldag plus de doorlooptijd van 5 tot 10
  // werkdagen haalt die vraag weg voordat hij ontstaat.
  const verwachtKlaar = verwachtKlaarVenster(order.ophalenVanaf)

  // Stappen: waar staat de klant in het proces?
  const stappen: Array<{ label: string; done: boolean; actief: boolean }> = [
    { label: 'Opgehaald', done: true, actief: false },
    { label: 'Voorinspectie: nameten, vlekken en bijzonderheden bekijken', done: wordtGewassen || Boolean(order.voorinspectie?.done), actief: !wordtGewassen && !order.voorinspectie?.done },
    { label: 'Reinigen en drogen', done: false, actief: wordtGewassen },
    { label: 'Jij kiest het aflevermoment', done: false, actief: false },
  ]

  return (
    <div>
      <StatusHeader title={title} subtitle={subtitle} />

      {/* PRIMAIRE KAART: wat gebeurt er nu */}
      <Card className="mb-md">
        <div className="flex items-center gap-md mb-md">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-mint-pale text-primary-dark flex-shrink-0">
            <Icon name="sparkles" />
          </div>
          <div>
            <p className="text-body font-semibold text-ink">
              {enkelKleed ? 'Je kleed is opgehaald' : 'Je kleden zijn opgehaald'}
            </p>
            <p className="text-body-sm text-ink-muted">
              {verwachtKlaar
                ? <>Naar verwachting klaar tussen <span className="font-medium text-ink">{verwachtKlaar.van}</span> en <span className="font-medium text-ink">{verwachtKlaar.tot}</span>. Daarna kies jij het aflevermoment.</>
                : KLEDA_CLAIMS.doorlooptijd}
            </p>
          </div>
        </div>

        <div className="pt-md border-t border-hairline space-y-xs">
          {stappen.map((stap) => (
            <div key={stap.label} className="flex items-start gap-sm">
              <span className={`mt-[3px] flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0 ${
                stap.done
                  ? 'bg-secondary/15 text-secondary'
                  : stap.actief
                    ? 'bg-primary/10 text-primary'
                    : 'bg-surface-soft text-ink-faint'
              }`}>
                {stap.done ? (
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className={`w-1.5 h-1.5 rounded-full ${stap.actief ? 'bg-primary animate-pulse' : 'bg-ink-faint/40'}`} />
                )}
              </span>
              <span className={`text-[13.5px] ${stap.actief ? 'text-ink font-medium' : stap.done ? 'text-ink-soft' : 'text-ink-faint'}`}>
                {stap.label}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Foto's en inspectie als stille rijen. Pas vanaf de voorinspectie: bij
          'Opgehaald' ligt het kleed nog in de bus en is er niets te zien. */}
      {(wordtGewassen || order.voorinspectie?.done) && (
        <ActionList className="mb-md">
          <FotoRij />
          <VoorinspectieRij order={order} />
        </ActionList>
      )}

      {/* Veelgestelde vragen in deze fase */}
      <FaqList items={KLEDA_FAQ.reinigen} className="mb-md" />

      <TrustLine className="px-xs" />
    </div>
  )
}

// =============================================================================
// AFGEROND VIEW
// =============================================================================

interface AfgerondViewProps {
  order: OrderData
  token?: string
}

export function AfgerondView({ order, token }: AfgerondViewProps) {
  return (
    <div>
      <StatusHeader
        title="Geniet er weer van."
        subtitle={
          <>
            {order.zelfHalen
              ? 'Je hebt je schone vloerkleed opgehaald.'
              : 'Je vloerkleed is schoon afgeleverd.'}{' '}
            Bedankt voor je vertrouwen, we hopen dat je er weer volop van geniet.
          </>
        }
      />

      {/* Volgende reiniging bovenaan (plan 2026-10-26, besluit Willem): het
          kleed ligt net weer, dit is het moment voor de vraag. De review volgt
          direct eronder; S10 vraagt de review als eerste. */}
      {TERUGKOM_KAART_AAN && token && (
        <VolgendeKeerKaart token={token} werkOrderId={order.werkOrderId} heeftMobiel={!!order.mobiel} bron="client-app" className="mb-md" />
      )}

      {/* Review CTA + Cinco cross-sell */}
      <ReviewCTA
        werkOrderId={order.werkOrderId}
        klantNaam={order.klantNaam?.split(' ')[0]}
      />

      {/* Stofzuig-tip: normaliseert opkomend diep vuil na de reiniging */}
      <Card className="mt-md">
        <p className="text-body font-medium text-ink mb-xs">Goed om te weten</p>
        <p className="text-body-sm text-ink-muted">
          Door de dieptereiniging staan de vezels van je kleed weer mooi open.
          Daardoor kan er de eerste weken nog wat diep vuil naar de oppervlakte
          komen. Dat is normaal. Geef je kleed na het uitrollen een goede
          stofzuigbeurt, dan blijft het op z'n mooist.
        </p>
      </Card>

      {/* Foto's en inspectie blijven permanent bereikbaar, ook zonder de
          oude e-mail-link */}
      <ActionList className="mt-md mb-md">
        <MijnKledaRij token={token} />
        <FotoRij />
        <VoorinspectieRij order={order} />
      </ActionList>

      <TrustLine className="mt-lg px-xs" />
    </div>
  )
}

// =============================================================================
// GEANNULEERD VIEW — order is geannuleerd; self-service heropenen
// Leeft bewust BUITEN de status-switch én PortalLayout: een geannuleerde order
// mag geen voortgangslijn of "opgehaald"-kaart tonen. Eigen gefocuste pagina.
// Na heropenen flipt de status naar 'Ophalen plannen' en valt de klant via de
// refetch direct in de kies-ophaalmoment-flow (incl. adres wijzigen).
// =============================================================================

interface GeannuleerdViewProps {
  order: OrderData
  onReactiveer: () => Promise<void>
  isReactiveren: boolean
  reactiveerError: string | null
}

export function GeannuleerdView({ order, onReactiveer, isReactiveren, reactiveerError }: GeannuleerdViewProps) {
  const itemCount = order.items?.filter(i => i.productCategory === 'Primary').length || 0
  const itemTekst = itemCount === 1 ? 'vloerkleed' : 'vloerkleden'
  const enkelKleed = itemCount <= 1

  const handleReactiveer = async () => {
    try {
      await onReactiveer()
    } catch {
      // fout wordt getoond via reactiveerError
    }
  }

  return (
    <PageLayout orderNummer={order.werkOrderNummer}>
      <div className="max-w-lg mx-auto">
        <StatusHeader
          title="Deze bestelling is geannuleerd"
          subtitle={
            <>
              {enkelKleed ? 'Je vloerkleed is' : 'Je vloerkleden zijn'} niet opgehaald
              en er staat op dit moment niets gepland.
            </>
          }
        />

        {/* PRIMAIRE TAAK-KAART: toch laten reinigen */}
        <Card className="mb-md">
          <p className="section-label mb-xs">Toch laten reinigen?</p>
          <p className="text-body text-ink-soft mb-md">
            We zetten je bestelling met één klik weer open. Daarna kies je zelf een
            nieuw ophaalmoment en kun je het adres aanpassen.
          </p>

          {reactiveerError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-md py-sm rounded-kleda-md mb-md text-body flex items-start gap-sm">
              <Icon name="alert" className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{reactiveerError}</span>
            </div>
          )}

          <button
            onClick={handleReactiveer}
            disabled={isReactiveren}
            className="btn-primary w-full py-md text-body font-medium"
          >
            {isReactiveren ? (
              <span className="flex items-center justify-center gap-sm">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Bezig met heropenen...
              </span>
            ) : (
              'Bestelling heropenen'
            )}
          </button>
        </Card>

        {/* Herkenning: wat zat er in deze bestelling */}
        {itemCount > 0 && (
          <p className="-mt-xs mb-sm px-xs text-[13px] text-ink-faint">
            Het ging om {itemCount} {itemTekst}.
          </p>
        )}

        {/* Liever persoonlijk contact — kalme uitweg naast de knop */}
        <p className="mb-md px-xs text-[13px] text-ink-faint">
          Liever even bellen?{' '}
          <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="font-semibold text-primary-dark">
            {KLEDA_CONTACT.telefoonWeergave}
          </a>
        </p>

        <TrustLine className="px-xs" />
      </div>
    </PageLayout>
  )
}

// =============================================================================
// ERROR VIEW
// =============================================================================

interface ErrorViewProps {
  error: string
  onRetry?: () => void
}

export function ErrorView({ error, onRetry }: ErrorViewProps) {
  return (
    <PageLayout>
      <StatusHeader
        title="Er ging iets mis"
        subtitle={error}
      />

      {onRetry && (
        <button onClick={onRetry} className="btn-primary w-full">
          Opnieuw proberen
        </button>
      )}
    </PageLayout>
  )
}

// =============================================================================
// ONBEKEND STATUS VIEW
// =============================================================================

// Neutrale fallback voor statussen die de app niet expliciet kent (edge-/
// terminale statussen). Bewust géén verzonnen voortgang of "opgehaald": een
// rustige, eerlijke melding met de gebruikelijke vertrouwensregel.
export function OnbekendView() {
  return (
    <div>
      <StatusHeader
        title="We werken aan je bestelling"
        subtitle={
          <>
            Je bestelling wordt verwerkt. Zodra er een volgende stap voor je
            klaarstaat, laten we het je weten per e-mail.
          </>
        }
      />

      <FaqList items={KLEDA_FAQ.reinigen} className="mb-md" />

      <TrustLine className="px-xs" />
    </div>
  )
}
